import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';

import { saveSchedule, setErrors } from 'bundles/backoffice/royalty_settings/store/royalty_schedules/actions';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';

import Modal from 'components/modal';
import Button from 'shared/button';
import ManagedInput from 'components/form/managed_input';
import ManagedFormGroup from 'components/form/managed_input/form_group';
import Tooltip from 'components/tooltip';

import { cleanTierValues, convertedScheduleTiers } from 'bundles/backoffice/helpers/royalties';

import TiersInputTable from './tiers_input_table';
import Errors from './errors';
import Warning from './warning';

const emptySchedule = {
  canEdit: true,
  tiers: [],
};

export function ScheduleModal({
  defaultRoyaltyPatterns,
  errors,
  labelsByRoyaltyPatternName,
  royaltySchedule,
  saveSchedule,
  saving,
  setErrors,
  show,
  onClose,
}) {
  const [schedule, setSchedule] = useState(emptySchedule);
  // The royalty pattern name is not actually validated (see: "custom"), so setting up mock validation here
  const [showPatternError, setShowPatternError] = useState();

  const handleClose = () => {
    setErrors([]);
    onClose();
  };

  useEffect(() => {
    setSchedule(royaltySchedule || emptySchedule);
    setShowPatternError(false);
  }, [show]);

  useEffect(() => {
    if (!saving && errors.length === 0) {
      handleClose();
    }
  }, [saving]);

  const handleSave = () => {
    setShowPatternError(false);

    if (!schedule.royaltyPatternName) {
      setShowPatternError(true);
    } else {
      saveSchedule(schedule);
    }
  };

  const handleChange = (changes) => {
    const updatedSchedule = { ...schedule, ...changes };

    if (changes.hasOwnProperty('royaltyPatternName')) {
      if (updatedSchedule.royaltyPatternName === 'custom') {
        updatedSchedule.tiers = [];
      } else {
        updatedSchedule.tiers = convertedScheduleTiers(updatedSchedule, defaultRoyaltyPatterns);
      }
    }

    if (changes.hasOwnProperty('tiers')) {
      updatedSchedule.tiers = cleanTierValues(updatedSchedule.tiers);
    }

    setSchedule(updatedSchedule);
  };

  return (
    <Modal
      backdrop="static"
      className="form-modal visible-overflow royalty-schedule-modal"
      show={show}
      onCancel={handleClose}
      onHide={handleClose}
    >
      <Modal.Header closeButton>
        {schedule.id ? 'Edit' : 'Add'} Royalty Schedule
      </Modal.Header>

      <Modal.Body>
        {!schedule.canEdit && <Warning />}

        {errors.length > 0 && <Errors />}

        <div className="form form-horizontal">
          <ManagedFormGroup
            label="Name"
            name="name"
            value={schedule.name}
            onChange={handleChange}
          />

          {showPatternError && (
            <div className="text-danger mb-2">
              Royalty Pattern Name is required
            </div>
          )}
          <ManagedFormGroup
            disabled={!schedule.canEdit}
            hasError={showPatternError}
            includeBlank
            label="Royalty Pattern"
            name="royaltyPatternName"
            required
            type="select"
            value={schedule.royaltyPatternName}
            onChange={handleChange}
          >
            {Object.keys(defaultRoyaltyPatterns).map((patternName, i) => (
              <option key={i} value={patternName}>{labelsByRoyaltyPatternName[patternName]}</option>
            ))}
            <option value="custom">Custom</option>
          </ManagedFormGroup>

          <ManagedInput
            name="companyDefault"
            type="checkbox"
            value={schedule.companyDefault}
            onChange={handleChange}
          >
            Set this royalty schedule as the company default
            <Tooltip
              content={
                `Royalties will be calculated according to the company's default schedule. There should only be one
                company default. If there is more than one, the oldest will be used. Changing the company default
                impacts future royalties only.`
              }
              dark
            >
              <FontAwesomeIcon className="text-muted ml-2" icon={faInfoCircle} />
            </Tooltip>
          </ManagedInput>

          <TiersInputTable
            schedule={schedule}
            onChange={handleChange}
          />
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button.Cancel name="cancel" onClick={handleClose} />
        <Button.Primary name="save" onClick={handleSave}>Save</Button.Primary>
      </Modal.Footer>
    </Modal>
  );
}

ScheduleModal.propTypes = {
  defaultRoyaltyPatterns: PropTypes.object,
  errors: PropTypes.array.isRequired,
  labelsByRoyaltyPatternName: PropTypes.object.isRequired,
  royaltySchedule: PropTypes.shape({
    canEdit: PropTypes.bool,
    companyDefault: PropTypes.bool,
    id: PropTypes.number,
    name: PropTypes.string,
    royaltyPatternName: PropTypes.string,
  }),
  saveSchedule: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  setErrors: PropTypes.func.isRequired,
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

const mapStateToProps = ({ royaltySchedules }) => ({
  defaultRoyaltyPatterns: royaltySchedules.defaultRoyaltyPatterns,
  labelsByRoyaltyPatternName: royaltySchedules.labelsByRoyaltyPatternName,
  errors: royaltySchedules.errors,
  saving: royaltySchedules.saving,
});

const mapDispatchToProps = {
  saveSchedule,
  setErrors,
};

export default connect(mapStateToProps, mapDispatchToProps)(ScheduleModal);
